"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  IconButton,
  Fade,
  Zoom,
  Slide,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import LoginIcon from "@mui/icons-material/Login";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BoltIcon from "@mui/icons-material/Bolt";
import FavoriteIcon from "@mui/icons-material/Favorite";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SchoolIcon from "@mui/icons-material/School";
import CelebrationIcon from "@mui/icons-material/Celebration";
import BusinessIcon from "@mui/icons-material/Business";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import QuizIcon from "@mui/icons-material/Quiz";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SpeedIcon from "@mui/icons-material/Speed";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import SettingsIcon from "@mui/icons-material/Settings";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ImageIcon from "@mui/icons-material/Image";
import TimerIcon from "@mui/icons-material/Timer";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { REGISTRATION_OPEN } from "./config";

/* ═══════════════════════════════════════════════════════
   ✨ KEYFRAMES
   ═══════════════════════════════════════════════════════ */

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-14px); }
`;

const pulseGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(79,70,229,.4); }
  70%  { box-shadow: 0 0 0 16px rgba(79,70,229,0); }
  100% { box-shadow: 0 0 0 0 rgba(79,70,229,0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const scrollBounce = keyframes`
  0%, 100% { transform: translateY(0); opacity: 1; }
  50%      { transform: translateY(10px); opacity: .4; }
`;

const blobAnim1 = keyframes`
  0%, 100% { transform: translate(0,0) scale(1); }
  33%      { transform: translate(25px,-35px) scale(1.05); }
  66%      { transform: translate(-15px,20px) scale(.97); }
`;

const blobAnim2 = keyframes`
  0%, 100% { transform: translate(0,0) scale(1); }
  33%      { transform: translate(-30px,25px) scale(.95); }
  66%      { transform: translate(20px,-15px) scale(1.06); }
`;

/* ═══════════════════════════════════════════════════════
   🎨 COLORS
   ═══════════════════════════════════════════════════════ */

// כשההרשמה סגורה הכפתורים עדיין מובילים למסך ההרשמה, שמציג את פרטי יצירת הקשר
const CTA_LABEL = REGISTRATION_OPEN ? "הרשמה בחינם" : "לפתיחת חשבון - צרו קשר";
const LOGIN_LABEL = REGISTRATION_OPEN ? "כניסה / הרשמה" : "כניסה";

const C = {
  primary: "#4f46e5", primaryL: "#6366f1", primaryXL: "#eef2ff",
  secondary: "#7c3aed",
  amber: "#f59e0b", emerald: "#10b981", cyan: "#06b6d4",
  pink: "#ec4899", rose: "#f43f5e",
  white: "#ffffff",
  bg: "#f8fafc", bgSoft: "#eef2ff",
  textDark: "#1e293b", textMid: "#475569", textLight: "#94a3b8",
  border: "#e2e8f0",
};

/* ═══════════════════════════════════════════════════════
   🧩 SHARED COMPONENTS
   ═══════════════════════════════════════════════════════ */

const HeroBlobs = () => (
  <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
    <Box sx={{ position: "absolute", width: { xs: 250, md: 400 }, height: { xs: 250, md: 400 }, borderRadius: "50%", filter: "blur(90px)", opacity: .45, background: "radial-gradient(circle, #c7d2fe, transparent)", top: "-8%", right: "-4%", animation: `${blobAnim1} 18s ease-in-out infinite` }} />
    <Box sx={{ position: "absolute", width: { xs: 200, md: 360 }, height: { xs: 200, md: 360 }, borderRadius: "50%", filter: "blur(90px)", opacity: .4, background: "radial-gradient(circle, #ddd6fe, transparent)", bottom: "0%", left: "-6%", animation: `${blobAnim2} 22s ease-in-out infinite` }} />
    <Box sx={{ position: "absolute", width: 250, height: 250, borderRadius: "50%", filter: "blur(80px)", opacity: .3, background: "radial-gradient(circle, #fde68a, transparent)", top: "30%", left: "50%", animation: `${blobAnim1} 20s 3s ease-in-out infinite` }} />
  </Box>
);

const ModernCard = styled(Card)(({ accent }) => ({
  background: "#fff",
  borderRadius: 20,
  border: "1px solid #e2e8f0",
  boxShadow: "0 4px 24px rgba(0,0,0,.06)",
  transition: "all .35s cubic-bezier(.25,.8,.25,1)",
  overflow: "visible",
  position: "relative",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: `0 16px 48px ${accent ? accent + "22" : "rgba(79,70,229,.12)"}`,
    borderColor: accent || C.primaryL,
  },
}));

/* ═══════════════════════════════════════════════════════
   🧭 NAVBAR — with scroll-to-section links
   ═══════════════════════════════════════════════════════ */

const aboutNavLinks = [
  { label: "מה זה", id: "whatis" },
  { label: "תכונות", id: "features" },
  { label: "למי מתאים", id: "usecases" },
  { label: "המסע", id: "timeline" },
  { label: "שאלות", id: "faq" },
];

const Navbar = () => {
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const h = () => {
      setScrolled(window.scrollY > 50);
      for (const link of [...aboutNavLinks].reverse()) {
        const el = document.getElementById(link.id);
        if (el && el.getBoundingClientRect().top < 200) {
          setActiveSection(link.id);
          return;
        }
      }
      setActiveSection("");
    };
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <Box sx={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
      py: 1.2, px: { xs: 2, md: 4 },
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: scrolled ? "rgba(255,255,255,.92)" : "transparent",
      backdropFilter: scrolled ? "blur(16px)" : "none",
      borderBottom: scrolled ? `1px solid ${C.border}` : "none",
      transition: "all .3s",
    }}>
      <Box onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        sx={{ display: "flex", alignItems: "center", gap: 1.2, cursor: "pointer", userSelect: "none" }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "14px", background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 12px ${C.primary}33` }}>
          <SportsEsportsIcon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: "1.5rem", color: C.primary, fontFamily: "'Varela Round', 'Rubik', sans-serif" }}>קליקרים</Typography>
      </Box>

      <Box sx={{ display: "flex", gap: { xs: .3, sm: 1 }, alignItems: "center" }}>
        {aboutNavLinks.map((l) => (
          <Button key={l.id} onClick={() => scrollTo(l.id)} sx={{
            color: activeSection === l.id ? C.primary : C.textMid,
            textTransform: "none", fontWeight: activeSection === l.id ? 800 : 600,
            borderRadius: 3, fontSize: { xs: ".72rem", sm: ".85rem" },
            position: "relative", minWidth: "auto", px: { xs: .8, sm: 1.5 },
            "&:hover": { color: C.primary, background: C.primaryXL },
            display: { xs: "none", md: "inline-flex" },
            "&::after": activeSection === l.id ? {
              content: '""', position: "absolute", bottom: 2, left: "20%", right: "20%", height: 3,
              borderRadius: 2, background: `linear-gradient(90deg, ${C.primary}, ${C.secondary})`,
            } : {},
          }}>{l.label}</Button>
        ))}
        <Button onClick={() => nav("/")} sx={{ color: C.textMid, textTransform: "none", fontWeight: 600, borderRadius: 3, "&:hover": { color: C.primary, background: C.primaryXL }, display: { xs: "none", sm: "inline-flex" }, fontSize: { xs: ".72rem", sm: ".85rem" } }}>
          <ArrowBackIcon sx={{ ml: .5, fontSize: 18 }} /> דף הבית
        </Button>
        <Button onClick={() => nav("/login")} sx={{ color: "#fff", textTransform: "none", fontWeight: 700, borderRadius: 50, px: { xs: 2, sm: 3 }, fontSize: { xs: ".8rem", sm: ".88rem" }, background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, boxShadow: `0 4px 14px ${C.primary}44`, "&:hover": { boxShadow: `0 6px 22px ${C.primary}55`, transform: "scale(1.03)" }, transition: "all .3s" }}>
          <LoginIcon sx={{ ml: .8, fontSize: 18 }} /> {LOGIN_LABEL}
        </Button>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   🚀 HERO
   ═══════════════════════════════════════════════════════ */

const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(true), []);

  return (
    <Box sx={{
      minHeight: { xs: "60vh", md: "70vh" }, display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden",
      background: `linear-gradient(170deg, #ffffff 0%, ${C.bgSoft} 40%, #f0f4ff 70%, #faf5ff 100%)`,
    }}>
      <HeroBlobs />

      <Fade in={loaded} timeout={1000}>
        <Box sx={{ textAlign: "center", zIndex: 5, px: 2, maxWidth: 800, mx: "auto", pt: 10 }}>
          <Zoom in={loaded} timeout={600}>
            <Box sx={{
              width: { xs: 80, md: 100 }, height: { xs: 80, md: 100 }, borderRadius: "28px", mx: "auto", mb: 3,
              background: `linear-gradient(135deg, ${C.primary}, ${C.secondary}, ${C.cyan})`,
              backgroundSize: "200% 200%",
              animation: `${gradientShift} 5s ease infinite, ${float} 5s ease-in-out infinite`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 12px 40px ${C.primary}33`,
            }}>
              <Typography sx={{ fontSize: { xs: 40, md: 50 } }}>ℹ️</Typography>
            </Box>
          </Zoom>

          <Typography variant="h1" sx={{
            fontSize: { xs: "2.5rem", sm: "3.8rem", md: "4.5rem" },
            fontWeight: 900, lineHeight: 1.15, mb: 2, color: C.textDark,
            fontFamily: "'Varela Round', 'Rubik', sans-serif",
          }}>
            הכירו את{" "}
            <Box component="span" sx={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.secondary}, ${C.cyan})`,
              backgroundSize: "200% auto", backgroundClip: "text",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              animation: `${shimmer} 4s linear infinite`,
            }}>קליקרים</Box>
          </Typography>

          <Typography sx={{
            fontSize: { xs: "1.05rem", sm: "1.2rem", md: "1.35rem" },
            color: C.textMid, fontWeight: 400, lineHeight: 1.9, maxWidth: 640, mx: "auto",
          }}>
            הפלטפורמה המתקדמת בישראל ליצירת משחקי חידון אינטראקטיביים — בואו ללמוד איך הכל עובד
          </Typography>

          <Box onClick={() => document.getElementById("whatis")?.scrollIntoView({ behavior: "smooth" })}
            sx={{ mt: 5, cursor: "pointer", color: C.textLight, animation: `${scrollBounce} 2s ease-in-out infinite` }}>
            <ArrowDownwardIcon sx={{ fontSize: 32 }} />
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   ❓ WHAT IS SECTION
   ═══════════════════════════════════════════════════════ */

const whatIsItems = [
  { emoji: "🎮", text: "פלטפורמה ליצירת משחקי חידון מרובי משתתפים בזמן אמת" },
  { emoji: "📱", text: "עובד מכל מכשיר — מובייל, טאבלט ומחשב — בלי התקנה" },
  { emoji: "⚡", text: "הרשמה ויצירת משחקים בחינם — רק הפעלה עולה תשלום סמלי" },
  { emoji: "👥", text: "תמיכה בקבוצות, ניקוד אישי ולוח מובילים חי" },
  { emoji: "🎵", text: "אפשרות להעלות מדיה: מוזיקה, תמונות וסרטונים" },
  { emoji: "🏆", text: "מסך ניצחון מרהיב עם אנימציות מיוחדות למקומות הראשונים" },
  { emoji: "🔗", text: "שיתוף בקישור אחד — השחקנים נכנסים מיד ומתחילים לשחק" },
  { emoji: "📊", text: "פאנל ניהול מתקדם לעקוב אחרי ביצועים ותוצאות" },
];

const WhatIsSection = () => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, []);

  return (
    <Box id="whatis" ref={ref} sx={{ py: { xs: 8, md: 12 }, px: 2, background: C.white, scrollMarginTop: 80 }}>
      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="💡 מה זה קליקרים?" sx={{ mb: 2, background: C.primaryXL, color: C.primary, fontWeight: 700, border: `1px solid ${C.primary}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              הכל על <Box component="span" sx={{ color: C.primary }}>קליקרים</Box>
            </Typography>
            <Typography sx={{ color: C.textMid, fontSize: "1.1rem", maxWidth: 560, mx: "auto" }}>
              קליקרים היא מערכת ישראלית ליצירת חידונים ומשחקי ידע מרובי משתתפים — מותאמת לעברית, קלה לשימוש, ועובדת מהר
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={2.5}>
          {whatIsItems.map((item, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Slide in={vis} direction={i % 2 === 0 ? "left" : "right"} timeout={400 + i * 80}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 2, p: 2.5,
                  borderRadius: 4, background: "#fff", border: `1px solid ${C.border}`,
                  boxShadow: "0 2px 12px rgba(0,0,0,.04)",
                  transition: "all .3s",
                  "&:hover": { transform: "translateX(-6px)", boxShadow: `0 6px 24px ${C.primary}12`, borderColor: C.primaryL },
                }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: C.primaryXL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>
                    {item.emoji}
                  </Box>
                  <Typography sx={{ color: C.textDark, fontWeight: 500, fontSize: ".98rem", lineHeight: 1.7 }}>
                    {item.text}
                  </Typography>
                </Box>
              </Slide>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   ⭐ DETAILED FEATURES
   ═══════════════════════════════════════════════════════ */

const detailedFeatures = [
  { icon: <QuizIcon sx={{ fontSize: 30 }} />, title: "שאלות מותאמות", desc: "צרו שאלות בכל נושא — מרובות תשובות, כמות נשלטת ומגוון אפשרויות ניקוד. בחרו כמה תשובות נכונות לכל שאלה.", color: C.primary, bg: "#eef2ff", emoji: "🎯" },
  { icon: <GroupsIcon sx={{ fontSize: 30 }} />, title: "ניהול קבוצות", desc: "חלקו שחקנים לקבוצות אוטומטית או ידנית. לכל קבוצה שם, צבע וניקוד נפרד בתחרות.", color: C.cyan, bg: "#ecfeff", emoji: "👥" },
  { icon: <EmojiEventsIcon sx={{ fontSize: 30 }} />, title: "מסך ניצחון", desc: "בסיום המשחק מוצג מסך ניצחון מרהיב עם אנימציות קונפטי, כוכבים ותמונות הזוכים.", color: C.amber, bg: "#fffbeb", emoji: "🏆" },
  { icon: <LeaderboardIcon sx={{ fontSize: 30 }} />, title: "לידרבורד חי", desc: "לוח מובילים בזמן אמת שמתעדכן אחרי כל שאלה. השחקנים רואים את מקומם מיד.", color: C.emerald, bg: "#ecfdf5", emoji: "📊" },
  { icon: <MusicNoteIcon sx={{ fontSize: 30 }} />, title: "העלאת מדיה", desc: "העלו מוזיקת רקע, תמונות ראשיות לשאלות וסרטונים. תמיכה מלאה בפורמטים נפוצים.", color: C.pink, bg: "#fdf2f8", emoji: "🎵" },
  { icon: <TimerIcon sx={{ fontSize: 30 }} />, title: "טיימר מותאם", desc: "הגדירו זמן תשובה לכל שאלה בנפרד. טיימר חזותי מרהיב שיוצר מתח ואתגר.", color: "#7c3aed", bg: "#f5f3ff", emoji: "⏱️" },
  { icon: <SettingsIcon sx={{ fontSize: 30 }} />, title: "פאנל ניהול", desc: "ממשק ניהול חזק — הוסיפו, ערכו ומחקו שאלות, נהלו שחקנים ועקבו אחרי ביצועים.", color: "#0ea5e9", bg: "#f0f9ff", emoji: "⚙️" },
  { icon: <ImageIcon sx={{ fontSize: 30 }} />, title: "תמונות שאלה", desc: "הוסיפו תמונה לכל שאלה — מתאים לזיהוי תמונות, חידונים גיאוגרפיים ועוד.", color: C.rose, bg: "#fff1f2", emoji: "🖼️" },
];

const DetailedFeaturesSection = () => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.05 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, []);

  return (
    <Box id="features" ref={ref} sx={{ py: { xs: 8, md: 12 }, px: 2, background: `linear-gradient(180deg, ${C.bg} 0%, ${C.bgSoft} 50%, ${C.bg} 100%)`, scrollMarginTop: 80 }}>
      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="⭐ תכונות מפורטות" sx={{ mb: 2, background: "#fffbeb", color: C.amber, fontWeight: 700, border: `1px solid ${C.amber}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              כל מה ש<Box component="span" sx={{ color: C.amber }}>קליקרים</Box> יודע לעשות
            </Typography>
            <Typography sx={{ color: C.textMid, fontSize: "1.1rem", maxWidth: 520, mx: "auto" }}>
              הנה סקירה מעמיקה של כל היכולות שהמערכת מציעה
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {detailedFeatures.map((f, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Zoom in={vis} timeout={300 + i * 100}>
                <ModernCard accent={f.color} sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    <Box sx={{ position: "absolute", top: -12, right: 14, width: 32, height: 32, borderRadius: "9px", background: f.bg, border: `1px solid ${f.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, boxShadow: `0 2px 6px ${f.color}15` }}>
                      {f.emoji}
                    </Box>
                    <Box sx={{ width: 60, height: 60, borderRadius: "18px", mx: "auto", mb: 2, background: f.bg, display: "flex", alignItems: "center", justifyContent: "center", color: f.color, transition: "all .3s", "&:hover": { transform: "rotate(5deg) scale(1.08)" } }}>
                      {f.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: C.textDark, mb: 1, fontSize: ".95rem" }}>{f.title}</Typography>
                    <Typography sx={{ color: C.textMid, lineHeight: 1.8, fontSize: ".84rem" }}>{f.desc}</Typography>
                  </CardContent>
                </ModernCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   🎯 USE CASES
   ═══════════════════════════════════════════════════════ */

const useCases = [
  {
    icon: <SchoolIcon sx={{ fontSize: 36 }} />, title: "חינוך והוראה",
    desc: "מורים משתמשים בקליקרים לחידונים בכיתה, חזרה למבחנים, שיעורים אינטראקטיביים וערבי הורים. התלמידים מתלהבים ולומדים בכיף!",
    color: C.primary, bg: "#eef2ff", emoji: "📚",
    examples: ["חידוני תנ\"ך", "חזרות למתמטיקה", "שיעורי אנגלית", "חידון היסטוריה"],
  },
  {
    icon: <CelebrationIcon sx={{ fontSize: 36 }} />, title: "אירועים ומסיבות",
    desc: "מנחי אירועים מוסיפים חידון כתחנה מרכזית באירוע — חתונות, בר/בת מצוות, ימי הולדת ואירועי חברה.",
    color: C.pink, bg: "#fdf2f8", emoji: "🎉",
    examples: ["חתונות", "בר/בת מצווה", "ימי הולדת", "מסיבות גן"],
  },
  {
    icon: <BusinessIcon sx={{ fontSize: 36 }} />, title: "ארגונים וחברות",
    desc: "צוותי HR וניהול משתמשים בקליקרים לגיבוש צוות, הכשרות, ימי כיף ובדיקות ידע של עובדים.",
    color: C.emerald, bg: "#ecfdf5", emoji: "💼",
    examples: ["גיבוש צוות", "הכשרת עובדים", "יום כיף", "מבחן ידע"],
  },
  {
    icon: <FamilyRestroomIcon sx={{ fontSize: 36 }} />, title: "משפחה וחברים",
    desc: "ערבי משפחה, חופשות, שבתות — כל הסיטואציות שבהן חידון מוסיף כיף ואנרגיה לקבוצה.",
    color: C.amber, bg: "#fffbeb", emoji: "👨‍👩‍👧‍👦",
    examples: ["ליל הסדר", "שבת בצהריים", "חופשה משפחתית", "ערב חברים"],
  },
];

const UseCasesSection = () => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.05 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, []);

  return (
    <Box id="usecases" ref={ref} sx={{ py: { xs: 8, md: 12 }, px: 2, background: C.white, scrollMarginTop: 80 }}>
      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="🎯 למי זה מתאים?" sx={{ mb: 2, background: "#ecfdf5", color: C.emerald, fontWeight: 700, border: `1px solid ${C.emerald}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              קליקרים מתאים ל<Box component="span" sx={{ color: C.emerald }}>כולם</Box>
            </Typography>
            <Typography sx={{ color: C.textMid, fontSize: "1.1rem", maxWidth: 520, mx: "auto" }}>
              מורים, מנחים, חברות ומשפחות — כל אחד ימצא שימוש מושלם
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={4}>
          {useCases.map((uc, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Slide in={vis} direction="up" timeout={400 + i * 150}>
                <ModernCard accent={uc.color} sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                      <Box sx={{ width: 64, height: 64, borderRadius: "20px", background: uc.bg, display: "flex", alignItems: "center", justifyContent: "center", color: uc.color, flexShrink: 0, transition: "all .3s", "&:hover": { transform: "scale(1.1) rotate(5deg)" } }}>
                        {uc.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: C.textDark, fontSize: "1.15rem" }}>{uc.title}</Typography>
                        <Typography sx={{ fontSize: 20 }}>{uc.emoji}</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ color: C.textMid, lineHeight: 1.9, fontSize: ".95rem", mb: 2.5 }}>{uc.desc}</Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {uc.examples.map((ex, j) => (
                        <Chip key={j} label={ex} size="small" sx={{ background: uc.bg, color: uc.color, fontWeight: 600, fontSize: ".78rem", border: `1px solid ${uc.color}22` }} />
                      ))}
                    </Box>
                  </CardContent>
                </ModernCard>
              </Slide>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   📋 TIMELINE
   ═══════════════════════════════════════════════════════ */

const timeline = [
  { title: "הרשמה חינם", desc: "יוצרים חשבון בקליקרים — לוקח 30 שניות וזה בחינם לגמרי!", emoji: "📝", color: C.primary, bg: "#eef2ff" },
  { title: "יצירת משחק", desc: "בוחרים שם, מוסיפים שאלות, תשובות, תמונות ומוזיקה — בחינם!", emoji: "🎮", color: C.pink, bg: "#fdf2f8" },
  { title: "בחירת חבילה", desc: "בוחרים חבילה בתשלום סמלי לפי מספר הפעלות ושחקנים — יש גם חבילת הדגמה חינמית!", emoji: "💎", color: "#7c3aed", bg: "#f5f3ff" },
  { title: "שיתוף קישור", desc: "שולחים קישור קצר לשחקנים — הם נכנסים ישר מהמכשיר", emoji: "🔗", color: C.emerald, bg: "#ecfdf5" },
  { title: "משחק חי!", desc: "המנחה שולט בקצב, השחקנים עונים והניקוד רץ בזמן אמת", emoji: "🎯", color: C.cyan, bg: "#ecfeff" },
  { title: "תוצאות ומנצחים", desc: "מסך ניצחון מרהיב, לוח מובילים ואפשרות לשחק שוב!", emoji: "🏆", color: C.amber, bg: "#fffbeb" },
];

const TimelineSection = () => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.05 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, []);

  return (
    <Box id="timeline" ref={ref} sx={{ py: { xs: 8, md: 12 }, px: 2, background: `linear-gradient(180deg, ${C.bg} 0%, ${C.bgSoft} 50%, ${C.bg} 100%)`, scrollMarginTop: 80 }}>
      <Container maxWidth="md">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="🚀 המסע שלכם" sx={{ mb: 2, background: "#fdf2f8", color: C.pink, fontWeight: 700, border: `1px solid ${C.pink}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              מההרשמה ועד <Box component="span" sx={{ color: C.pink }}>הניצחון</Box>
            </Typography>
            <Typography sx={{ color: C.textMid, fontSize: "1.1rem", maxWidth: 480, mx: "auto" }}>
              שישה צעדים פשוטים — והמשחק שלכם חי!
            </Typography>
          </Box>
        </Fade>

        <Box sx={{ position: "relative" }}>
          {/* vertical line */}
          <Box sx={{
            display: { xs: "none", md: "block" },
            position: "absolute", right: "50%", top: 0, bottom: 0, width: 3,
            background: `linear-gradient(180deg, ${C.primary}22, ${C.pink}22, ${C.amber}22)`,
            borderRadius: 2, transform: "translateX(50%)",
          }} />

          {timeline.map((step, i) => (
            <Slide key={i} in={vis} direction={i % 2 === 0 ? "left" : "right"} timeout={400 + i * 120}>
              <Box sx={{
                display: "flex", alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "row", md: i % 2 === 0 ? "row" : "row-reverse" },
                mb: 4, position: "relative",
              }}>
                {/* content card */}
                <Box sx={{
                  flex: 1, p: 3, borderRadius: 5, background: "#fff",
                  border: `1px solid ${C.border}`, boxShadow: "0 4px 20px rgba(0,0,0,.05)",
                  transition: "all .3s", mx: { xs: 0, md: 3 },
                  "&:hover": { transform: "translateY(-4px)", boxShadow: `0 8px 30px ${step.color}15`, borderColor: step.color + "44" },
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: step.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>
                      {step.emoji}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: C.textDark, fontSize: "1.05rem" }}>{step.title}</Typography>
                  </Box>
                  <Typography sx={{ color: C.textMid, lineHeight: 1.8, fontSize: ".9rem", pr: { xs: 0, md: 7 } }}>{step.desc}</Typography>
                </Box>

                {/* center dot (desktop) */}
                <Box sx={{
                  display: { xs: "none", md: "flex" },
                  position: "absolute", right: "50%", transform: "translateX(50%)",
                  width: 40, height: 40, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${step.color}, ${step.color}cc)`,
                  alignItems: "center", justifyContent: "center",
                  color: "#fff", fontWeight: 900, fontSize: ".9rem",
                  boxShadow: `0 4px 12px ${step.color}44`, zIndex: 2,
                  border: "3px solid #fff",
                }}>
                  {i + 1}
                </Box>

                {/* spacer for opposite side */}
                <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
              </Box>
            </Slide>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   ❓ FAQ
   ═══════════════════════════════════════════════════════ */

const faqItems = [
  { q: "כמה עולה קליקרים?", a: "ההרשמה, יצירת משחקים והוספת שאלות — הכל בחינם לגמרי! רק הפעלת משחק (מעבר למסך ההמתנה ולמשחק חי) דורשת חבילה בתשלום סמלי. יש גם חבילת הדגמה חינמית ללקוחות חדשים!" },
  { q: "כמה שחקנים יכולים להשתתף?", a: "מספר השחקנים תלוי בחבילה שרכשתם. יש חבילות מ-12 שחקנים ועד מאות שחקנים. המערכת מותאמת לעומסים." },
  { q: "האם צריך להתקין אפליקציה?", a: "לא! קליקרים עובד ישירות מהדפדפן. השחקנים פותחים את הקישור שנשלח להם ומתחילים לשחק מיד — בלי התקנות." },
  { q: "האם המערכת תומכת בעברית?", a: "בהחלט! קליקרים נבנתה מאפס עבור עברית, עם תמיכה מלאה בכתיבה מימין לשמאל ובפונטים מותאמים." },
  { q: "האם אפשר להוסיף תמונות ומוזיקה?", a: "כן! אפשר להעלות תמונות לכל שאלה, מוזיקת רקע למשחק, ואפילו סרטונים. המדיה מתנגנת אוטומטית במהלך המשחק." },
  { q: "מה קורה בסוף המשחק?", a: "בסוף כל משחק מוצג מסך ניצחון מרהיב עם אנימציות, קונפטי, לוח מובילים ותמונות הזוכים. אפשר גם לשחק שוב!" },
];

const FAQSection = () => {
  const [vis, setVis] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.05 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, []);

  const handleChange = (panel) => (_, isExpanded) => setExpanded(isExpanded ? panel : false);

  return (
    <Box id="faq" ref={ref} sx={{ py: { xs: 8, md: 12 }, px: 2, background: C.white, scrollMarginTop: 80 }}>
      <Container maxWidth="md">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="❓ שאלות נפוצות" sx={{ mb: 2, background: C.primaryXL, color: C.primary, fontWeight: 700, border: `1px solid ${C.primary}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              שאלות ו<Box component="span" sx={{ color: C.primary }}>תשובות</Box>
            </Typography>
          </Box>
        </Fade>

        {faqItems.map((item, i) => (
          <Zoom in={vis} timeout={300 + i * 100} key={i}>
            <Accordion
              expanded={expanded === `panel${i}`}
              onChange={handleChange(`panel${i}`)}
              sx={{
                mb: 2, borderRadius: "16px !important", border: `1px solid ${C.border}`,
                boxShadow: "0 2px 12px rgba(0,0,0,.04)",
                "&:before": { display: "none" },
                overflow: "hidden",
                transition: "all .3s",
                "&:hover": { borderColor: C.primaryL, boxShadow: `0 4px 20px ${C.primary}10` },
                ...(expanded === `panel${i}` && { borderColor: C.primary + "44", boxShadow: `0 6px 28px ${C.primary}12` }),
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: C.primary }} />}
                sx={{ py: 1, px: 3 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "10px",
                    background: expanded === `panel${i}` ? `linear-gradient(135deg, ${C.primary}, ${C.secondary})` : C.primaryXL,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: expanded === `panel${i}` ? "#fff" : C.primary,
                    fontWeight: 800, fontSize: ".85rem",
                    transition: "all .3s",
                  }}>
                    {i + 1}
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: C.textDark, fontSize: "1rem" }}>{item.q}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                <Typography sx={{ color: C.textMid, lineHeight: 1.9, fontSize: ".95rem", pr: 6.5 }}>{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          </Zoom>
        ))}
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   🎯 CTA
   ═══════════════════════════════════════════════════════ */

const CTASection = () => {
  const nav = useNavigate();
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.1 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, []);

  return (
    <Box ref={ref} sx={{
      py: { xs: 8, md: 12 }, px: 2, position: "relative", overflow: "hidden",
      background: `linear-gradient(170deg, #f0f4ff 0%, #faf5ff 40%, #fef3c7 100%)`,
    }}>
      <Box sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: `radial-gradient(circle, ${C.primary}15, transparent)`, top: -60, right: -60, filter: "blur(40px)" }} />
      <Box sx={{ position: "absolute", width: 250, height: 250, borderRadius: "50%", background: `radial-gradient(circle, ${C.pink}12, transparent)`, bottom: -40, left: -40, filter: "blur(40px)" }} />

      <Fade in={vis} timeout={700}>
        <Box sx={{ textAlign: "center", position: "relative", zIndex: 2 }}>
          <Typography sx={{ fontSize: 56, mb: 2 }}>🚀</Typography>
          <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 2, fontSize: { xs: "1.8rem", md: "2.8rem" } }}>
            מוכנים ל<Box component="span" sx={{ color: C.primary }}>התחיל</Box>?
          </Typography>
          <Typography sx={{ color: C.textMid, fontSize: "1.1rem", mb: 5, maxWidth: 480, mx: "auto" }}>
            הצטרפו עכשיו וצרו את המשחק הראשון שלכם תוך דקות
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button onClick={() => nav("/login?tab=register")} sx={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
              backgroundSize: "200% 200%",
              animation: `${gradientShift} 4s ease infinite, ${pulseGlow} 3s infinite`,
              color: "#fff", fontWeight: 800, fontSize: "1.15rem",
              borderRadius: 50, px: 5, py: 2, textTransform: "none",
              boxShadow: `0 8px 28px ${C.primary}44`,
              "&:hover": { transform: "scale(1.04)", boxShadow: `0 12px 36px ${C.primary}55` },
              transition: "all .3s",
            }}>
              <RocketLaunchIcon sx={{ ml: 1 }} /> {CTA_LABEL}
            </Button>
            <Button onClick={() => nav("/")} sx={{
              color: C.primary, fontWeight: 700, fontSize: "1rem",
              borderRadius: 50, px: 4, py: 1.5, border: `2px solid ${C.primary}44`,
              textTransform: "none", background: "#fff",
              "&:hover": { background: C.primaryXL, borderColor: C.primary },
            }}>
              <ArrowBackIcon sx={{ ml: 1, fontSize: 20 }} /> חזרה לדף הבית
            </Button>
          </Box>
          <Box sx={{ mt: 4, display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap" }}>
            {[
              { icon: <CheckCircleIcon sx={{ fontSize: 16, color: C.emerald }} />, text: "ללא כרטיס אשראי" },
              { icon: <BoltIcon sx={{ fontSize: 16, color: C.amber }} />, text: "התחלה מיידית" },
              { icon: <FavoriteIcon sx={{ fontSize: 16, color: C.pink }} />, text: "תמיכה מלאה" },
            ].map((b, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: .7, color: C.textMid, fontSize: ".85rem" }}>{b.icon} <span>{b.text}</span></Box>
            ))}
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   📌 FOOTER
   ═══════════════════════════════════════════════════════ */

const Footer = () => {
  const nav = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <Box component="footer" sx={{ py: 6, px: 3, background: C.textDark, color: "#fff" }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: "12px", background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SportsEsportsIcon sx={{ color: "#fff", fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontWeight: 900, fontSize: "1.3rem", color: "#fff" }}>קליקרים</Typography>
            </Box>
            <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: ".9rem", lineHeight: 1.8, maxWidth: 300 }}>
              הפלטפורמה המובילה בישראל ליצירת משחקי חידון אינטראקטיביים מרובי משתתפים.
            </Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography sx={{ fontWeight: 700, color: "#fff", mb: 2, fontSize: ".95rem" }}>ניווט</Typography>
            {[{ label: "דף הבית", action: () => nav("/") }, { label: "אודות", action: () => { window.scrollTo({ top: 0, behavior: "smooth" }); } }, { label: "כניסה", action: () => nav("/login") }].map((l, i) => (
              <Typography key={i} onClick={l.action} sx={{ color: "rgba(255,255,255,.5)", cursor: "pointer", fontSize: ".88rem", mb: 1.2, "&:hover": { color: "#fff" }, transition: "color .2s" }}>{l.label}</Typography>
            ))}
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography sx={{ fontWeight: 700, color: "#fff", mb: 2, fontSize: ".95rem" }}>סעיפים</Typography>
            {aboutNavLinks.map((l, i) => (
              <Typography key={i} onClick={() => scrollTo(l.id)} sx={{ color: "rgba(255,255,255,.5)", cursor: "pointer", fontSize: ".88rem", mb: 1.2, "&:hover": { color: "#fff" }, transition: "color .2s" }}>{l.label}</Typography>
            ))}
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography sx={{ fontWeight: 700, color: "#fff", mb: 2, fontSize: ".95rem" }}>מידע נוסף</Typography>
            <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: ".88rem", lineHeight: 1.9 }}>
              קליקרים — פלטפורמת חידונים בעברית, חינמית, ללא התקנה, מותאמת לכל מכשיר.
            </Typography>
          </Grid>
        </Grid>
        <Box sx={{ borderTop: "1px solid rgba(255,255,255,.1)", pt: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography sx={{ color: "rgba(255,255,255,.3)", fontSize: ".82rem" }}>© {new Date().getFullYear()} קליקרים — כל הזכויות שמורות</Typography>
          <Typography sx={{ color: "rgba(255,255,255,.2)", fontSize: ".75rem" }}>נבנה עם ❤️ לחוויות משחק מדהימות</Typography>
        </Box>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   ⬆️ BACK TO TOP
   ═══════════════════════════════════════════════════════ */

const BackToTop = () => {
  const [show, setShow] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const h = () => {
      setShow(window.scrollY > 500);
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  if (!show) return null;

  return (
    <Zoom in>
      <Box sx={{ position: "fixed", bottom: 24, left: 24, zIndex: 100 }}>
        <Box sx={{ position: "relative", width: 54, height: 54 }}>
          <svg width="54" height="54" style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}>
            <circle cx="27" cy="27" r="24" fill="none" stroke="rgba(255,255,255,.2)" strokeWidth="3" />
            <circle cx="27" cy="27" r="24" fill="none" stroke="#fff" strokeWidth="3"
              strokeDasharray={`${2 * Math.PI * 24}`}
              strokeDashoffset={`${2 * Math.PI * 24 * (1 - progress / 100)}`}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset .1s" }}
            />
          </svg>
          <IconButton onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{
            position: "absolute", top: 2, left: 2, width: 50, height: 50,
            background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, color: "#fff",
            boxShadow: `0 4px 18px ${C.primary}44`,
            "&:hover": { transform: "scale(1.1)" }, transition: "all .3s",
          }}>
            <KeyboardArrowUpIcon />
          </IconButton>
        </Box>
      </Box>
    </Zoom>
  );
};

/* ═══════════════════════════════════════════════════════
   📄 MAIN ABOUT PAGE
   ═══════════════════════════════════════════════════════ */

const AboutPage = () => (
  <Box sx={{ width: "100%", minHeight: "100vh" }}>
    <Navbar />
    <HeroSection />
    <WhatIsSection />
    <DetailedFeaturesSection />
    <UseCasesSection />
    <TimelineSection />
    <FAQSection />
    <CTASection />
    <Footer />
    <BackToTop />
  </Box>
);

export default AboutPage;
